<template>
  <v-card>
    <v-toolbar
      class="settings-window-toolbar"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.Reservation.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.Reservation.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.Reservation.Toolbar.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.Reservation.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      style="overflow-y: auto;"
    >
      <template v-if="viewData">
        <v-form
          ref="settingsForm"
          v-model="settingsFormValid"
          autocomplete="off"
          @submit.prevent
        >
          <v-card flat>
            <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
              <v-icon class="pr-3">
                {{ $t('Settings.Reservation.Section.Duration.Icon') }}
              </v-icon>
              <h5 class="text-body-2 font-weight-bold">
                <lang-search-highlight
                  :lang-text="$t('Settings.Reservation.Section.Duration.Title')"
                  :search-text="langSearchText"
                  highlight
                />
              </h5>
            </v-card-title>

            <v-divider />

            <v-card-text>
              <v-container
                class="pa-0"
                fluid
              >
                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.TimeIntervalOnline.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.TimeIntervalOnline.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.TimeIntervalOnline"
                      :disabled="!userCanManage"
                      :items="viewData.TimeIntervalOnlineValues"
                      :label="$t('Settings.Reservation.Section.Duration.Field.TimeIntervalOnline.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.TimeSlotBetween.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.TimeSlotBetween.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.TimeSlotBetween"
                      :disabled="!userCanManage"
                      :items="viewData.TimeSlotBetweenValues"
                      :label="$t('Settings.Reservation.Section.Duration.Field.TimeSlotBetween.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.AllowedMinutesStep.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.AllowedMinutesStep.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.AllowedMinutesStep"
                      :disabled="!userCanManage"
                      :items="viewData.AllowedMinutesStepValues"
                      :label="$t('Settings.Reservation.Section.Duration.Field.AllowedMinutesStep.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.MaxDaysInAdvance.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.MaxDaysInAdvance.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.MaxDaysInAdvance"
                      :disabled="!userCanManage"
                      :items="viewData.MaxDaysInAdvanceValues"
                      :label="$t('Settings.Reservation.Section.Duration.Field.MaxDaysInAdvance.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.MinTimeInAdvance.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.MinTimeInAdvance.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.MinTimeInAdvance"
                      :disabled="!userCanManage"
                      :items="viewData.MinTimeInAdvanceValues"
                      :label="$t('Settings.Reservation.Section.Duration.Field.MinTimeInAdvance.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.DurationDefault.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.DurationDefault.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.DurationDefault"
                      :disabled="!userCanManage"
                      :items="viewData.DurationDefaultValues"
                      :label="$t('Settings.Reservation.Section.Duration.Field.DurationDefault.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.UseDurationByPeople.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Duration.Field.UseDurationByPeople.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-switch
                      v-model="viewData.UseDurationByPeople"
                      :disabled="!userCanManage"
                      :label="viewData.UseDurationByPeople ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col
                    v-if="viewData.UseDurationByPeople"
                    cols="12"
                  >
                    <v-simple-table>
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t('Settings.Reservation.Section.Duration.Field.DurationByPeople.MinPeople') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Settings.Reservation.Section.Duration.Field.DurationByPeople.MaxPeople') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Settings.Reservation.Section.Duration.Field.DurationByPeople.Duration') }}
                          </th>
                          <th class="text-right" />
                        </tr>
                      </thead>

                      <tbody>
                        <template v-for="(item, idx) in viewData.DurationByPeople">
                          <tr :key="`duration-by-people-${idx}`">
                            <td>
                              <v-text-field
                                v-model="item.min"
                                v-mask="'####'"
                                class="d-inline-block"
                                dense
                                flat
                                hide-details
                                min="1"
                                outlined
                                single-line
                                step="1"
                                type="number"
                              />
                            </td>

                            <td>
                              <v-text-field
                                v-model="item.max"
                                v-mask="'####'"
                                class="d-inline-block"
                                dense
                                flat
                                hide-details
                                min="1"
                                outlined
                                single-line
                                step="1"
                                type="number"
                              />
                            </td>

                            <td>
                              <v-select
                                v-model="item.duration"
                                :disabled="!userCanManage"
                                :items="viewData.DurationDefaultValues"
                                class="d-inline-block"
                                dense
                                flat
                                hide-details
                                item-text="Name"
                                item-value="Value"
                                outlined
                                single-line
                              />
                            </td>

                            <td class="text-right">
                              <v-btn
                                depressed
                                icon
                                small
                                @click="removeDurationRule(idx)"
                              >
                                <v-icon left>
                                  mdi mdi-minus-circle-outline
                                </v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </v-simple-table>

                    <div class="mt-2 text-right">
                      <v-btn
                        color="primary"
                        depressed
                        @click="addDurationRule"
                      >
                        <v-icon left>
                          {{ $t('Settings.Reservation.Section.Duration.Field.DurationByPeople.Button.Add.Icon') }}
                        </v-icon>
                        {{ $t('Settings.Reservation.Section.Duration.Field.DurationByPeople.Button.Add.Title') }}
                      </v-btn>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>

          <v-card flat>
            <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
              <v-icon class="pr-3">
                {{ $t('Settings.Reservation.Section.People.Icon') }}
              </v-icon>
              <h5 class="text-body-2 font-weight-bold">
                <lang-search-highlight
                  :lang-text="$t('Settings.Reservation.Section.People.Title')"
                  :search-text="langSearchText"
                  highlight
                />
              </h5>
            </v-card-title>

            <v-divider />

            <v-card-text>
              <v-container
                class="pa-0"
                fluid
              >
                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.People.Field.MinPeopleOnline.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.People.Field.MinPeopleOnline.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.MinPeopleOnline"
                      :disabled="!userCanManage"
                      :items="viewData.MinPeopleOnlineValues"
                      :label="$t('Settings.Reservation.Section.People.Field.MinPeopleOnline.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.People.Field.MaxPeopleOnline.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.People.Field.MaxPeopleOnline.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.MaxPeopleOnline"
                      :disabled="!userCanManage"
                      :items="viewData.MaxPeopleOnlineValues"
                      :label="$t('Settings.Reservation.Section.People.Field.MaxPeopleOnline.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.People.Field.PersonsThreshold.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.People.Field.PersonsThreshold.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-select
                      v-model="viewData.PersonsThreshold"
                      :disabled="!userCanManage"
                      :items="viewData.PersonsThresholdValues"
                      :label="$t('Settings.Reservation.Section.People.Field.PersonsThreshold.Title')"
                      class="ma-0"
                      hide-details
                      item-text="Name"
                      item-value="Value"
                      required
                      single-line
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>

          <v-card flat>
            <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
              <v-icon class="pr-3">
                {{ $t('Settings.Reservation.Section.Manage.Icon') }}
              </v-icon>
              <h5 class="text-body-2 font-weight-bold">
                <lang-search-highlight
                  :lang-text="$t('Settings.Reservation.Section.Manage.Title')"
                  :search-text="langSearchText"
                  highlight
                />
              </h5>
            </v-card-title>

            <v-divider />

            <v-card-text>
              <v-container
                class="pa-0"
                fluid
              >
                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Manage.Field.AutoUpdateTables.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Manage.Field.AutoUpdateTables.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-switch
                      v-model="viewData.AutoUpdateTables"
                      :disabled="!userCanManage"
                      :label="viewData.AutoUpdateTables ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Manage.Field.TableOverwrite.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Manage.Field.TableOverwrite.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-switch
                      v-model="viewData.TableOverwrite"
                      :disabled="!userCanManage"
                      :label="viewData.TableOverwrite ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>

                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Manage.Field.OnlineBooking.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Reservation.Section.Manage.Field.OnlineBooking.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink shrink-col-min-width">
                    <v-switch
                      v-model="viewData.OnlineBooking"
                      :disabled="!userCanManage"
                      :label="viewData.OnlineBooking ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-form>
      </template>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />
      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        to="/system-settings"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>

      <v-btn
        v-if="userCanManage"
        :disabled="!viewData || isSaving"
        :loading="isSaving"
        class="green white--text elevation-0"
        @click="onSaveClick"
      >
        {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import LangSearchHighlightMixin from '@/lang/highlight/langSearchHighlightMixin.js'
import LangSearchHighlight from '@/lang/highlight/LangSearchHighlight.vue'

export default {
  components: {LangSearchHighlight},
  mixins    : [AppData, Auth, LangSearchHighlightMixin],
  data() {
    return {
      settingsFormValid: false,
      isSaving         : false,
      viewData         : null,
      validationRules  : {}
    }
  },
  computed: {},
  watch   : {},
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Settings.Reservations.Get, this.onGetDataResponse)
    this.$bus.$on(window.SocketCommand.Settings.Reservations.Save, this.onSaveDataResponse)
    this.getData()
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Settings.Reservations.Get, this.onGetDataResponse)
    this.$bus.$off(window.SocketCommand.Settings.Reservations.Save, this.onSaveDataResponse)
  },
  methods: {
    getData() {
      window.callAS(window.SocketCommand.Settings.Reservations.Get, {})
    },

    onGetDataResponse(result) {
      this.viewData = result
    },

    addDurationRule() {
      if (!this.viewData?.DurationByPeople) return

      this.viewData.DurationByPeople.push({
        min     : 1,
        max     : 2,
        duration: 120
      })
    },

    removeDurationRule(index) {
      if (parseInt(index) < 0) return

      this.viewData.DurationByPeople.splice(parseInt(index), 1)
    },

    onSaveClick() {
      this.$refs?.settingsForm && this.$refs.settingsForm.validate()

      if (this.settingsFormValid) {
        this.isSaving = true
        const payload = JSON.parse(JSON.stringify(this.viewData, (key, value) => key.endsWith('Values') ? undefined : value))

        window.callAS(window.SocketCommand.Settings.Reservations.Save, payload)
      }
    },

    onSaveDataResponse(result) {
      this.isSaving = false

      if (result.status) {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: result.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Documents.Notification'),
          type: 'error',
          icon: 'warning'
        })
      }
    }
  }
}
</script>

<style scoped>
.shrink-col-min-width {
  min-width: 260px;
}
</style>
